import React from "react";
import { Outlet, NavLink, Link, useNavigate } from "react-router-dom";
import { Home, Users, MessageCircle, HeartPulse, User, LogOut, Sparkles, Settings, Flame, Compass, WandSparkles, Bot, BriefcaseBusiness, Search, Target, Brain } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { api, fileUrl } from "@/lib/api";
import NotificationsBell from "@/components/NotificationsBell";
import CalmBadge from "@/components/CalmBadge";
import OnboardingModal from "@/components/OnboardingModal";

const baseNavItems = [
  { to: "/for-you", label: "For You", icon: WandSparkles, tid: "nav-for-you", flag: "haeven4_for_you" },
  { to: "/memory", label: "Your Memory", icon: Brain, tid: "nav-memory", flag: "haeven4_for_you" },
  { to: "/assistant", label: "Companion", icon: Bot, tid: "nav-assistant", flag: "haeven4_companion" },
  { to: "/directory", label: "Directory", icon: Search, tid: "nav-directory", flag: "haeven4_directory" },
  { to: "/outcomes", label: "Outcomes", icon: Target, tid: "nav-outcomes", flag: "haeven4_outcomes" },
  { to: "/creator-studio", label: "Creator Studio", icon: BriefcaseBusiness, tid: "nav-creator-studio", flag: "haeven4_creator_tools" },
  { to: "/feed", label: "Feed", icon: Home, tid: "nav-feed" },
  { to: "/trending", label: "Trending", icon: Flame, tid: "nav-trending" },
  { to: "/discover", label: "Discover", icon: Compass, tid: "nav-discover" },
  { to: "/communities", label: "Communities", icon: Users, tid: "nav-communities" },
  { to: "/messages", label: "Messages", icon: MessageCircle, tid: "nav-messages" },
  { to: "/wellness", label: "Wellness", icon: HeartPulse, tid: "nav-wellness" },
  { to: "/premium", label: "Premium", icon: Sparkles, tid: "nav-premium" },
  { to: "/profile", label: "Profile", icon: User, tid: "nav-profile" },
  { to: "/settings", label: "Settings", icon: Settings, tid: "nav-settings" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isPremium = !!user?.premium?.active;
  const userId = user?.id;
  const [features, setFeatures] = React.useState({});

  React.useEffect(() => {
    let active = true;
    if (userId) {
      api.get("/features")
        .then((r) => { if (active) setFeatures(r.data || {}); })
        .catch(() => { if (active) setFeatures({}); });
    } else {
      setFeatures({});
    }
    return () => { active = false; };
  }, [userId]);

  const navItems = baseNavItems.filter((item) => !item.flag || features[item.flag]);
  const homePath = features.haeven4_for_you ? "/for-you" : "/feed";
  const exactPaths = ["/feed", "/for-you", "/memory", "/assistant", "/directory", "/outcomes", "/creator-studio"];

  const doLogout = () => { logout(); navigate("/"); };

  return (
    <div className="min-h-screen bg-background grain pb-24 md:pb-0">
      <header className="sticky top-0 z-30 bg-background/90 backdrop-blur border-b-2 border-foreground">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-3 flex items-center justify-between">
          <Link to={homePath} className="flex items-center gap-2 font-display text-2xl tracking-tight" data-testid="top-logo">
            <span className="inline-block w-6 h-6 bg-primary border-2 border-foreground rounded-md shadow-brutal-sm" />
            HAEVEN
          </Link>
          <div className="flex items-center gap-3">
            <NotificationsBell />
            <Link to="/profile" data-testid="top-avatar" className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-full border-2 border-foreground overflow-hidden bg-sage flex items-center justify-center font-bold">
                {user?.avatar_path ? <img src={fileUrl(user.avatar_path)} className="w-full h-full object-cover" alt="" /> : (user?.display_name?.[0] || "?")}
              </div>
              <span className="hidden sm:flex items-center gap-2 font-semibold text-sm">
                {user?.display_name}
                {isPremium && <CalmBadge size="xs" />}
              </span>
            </Link>
            <button data-testid="logout-btn" onClick={doLogout} className="brutal-btn p-2 bg-card" title="Log out">
              <LogOut size={18} strokeWidth={2.5} />
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 md:px-6 py-6 md:grid md:grid-cols-[220px_1fr] md:gap-8">
        <aside className="hidden md:block sticky top-24 h-fit">
          <nav className="space-y-2">
            {navItems.map(({ to, label, icon: Icon, tid }) => (
              <NavLink key={to} to={to} end={exactPaths.includes(to)} data-testid={tid}
                className={({ isActive }) => `flex items-center gap-3 px-4 py-2 rounded-xl border-2 border-foreground font-semibold transition-all ${isActive ? "bg-primary text-primary-foreground shadow-brutal-sm" : "bg-card hover:bg-secondary"}`}>
                <Icon size={18} strokeWidth={2.5} /> {label}
              </NavLink>
            ))}
          </nav>
        </aside>

        <main><Outlet /></main>
      </div>

      <OnboardingModal />

      <nav className="md:hidden fixed bottom-4 left-1/2 -translate-x-1/2 z-40 flex items-center gap-0.5 brutal-card bg-card px-1.5 py-1.5 max-w-[calc(100vw-1rem)] overflow-x-auto">
        {navItems.map(({ to, icon: Icon, tid }) => (
          <NavLink key={to} to={to} end={exactPaths.includes(to)} data-testid={`mobile-${tid}`}
            className={({ isActive }) => `p-2 rounded-lg shrink-0 ${isActive ? "bg-primary text-primary-foreground" : ""}`}>
            <Icon size={19} strokeWidth={2.5} />
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
